import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Composer } from '../components/Composer.tsx';
import { MessageThread } from '../components/MessageThread.tsx';
import { SessionDetails } from '../components/SessionDetails.tsx';
import { SessionList } from '../components/SessionList.tsx';
import type { SessionCounts, SessionTab } from '../components/SessionList.tsx';
import { EmptyState, Spinner } from '../components/ui.tsx';
import { MessageSquareIcon } from '../components/icons.tsx';
import { api } from '../lib/api.ts';
import type { AttachmentDto, MessageDto, Paged, SessionDetailDto, SessionListItemDto } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { refreshSessions, refreshUnread } from '../lib/sessionCache.ts';
import { useRealtime, useRealtimeEvent } from '../lib/realtime.tsx';

interface PresetResponse {
  id: number;
  title: string;
  content: string;
  shortcut: string | null;
}

/** Matches the server's default page size for `/sessions`. */
const SESSION_PAGE_SIZE = 50;

/**
 * How many conversations in a bucket still need attention.
 *
 * The badge counts unread conversations, not everything in the bucket: a total
 * never reaches zero, so it reads as decoration rather than as something to
 * act on.
 */
function countUnreadChats(items: SessionListItemDto[]): number {
  return items.filter((item) => item.unread > 0).length;
}

/**
 * Which tab a conversation is filed under.
 *
 * The same rule the list uses to split one page of rows: archived wins, then
 * an unassigned conversation is in the queue and anything else is somebody's.
 * `null` when there is nothing open, or when its details have not arrived yet
 * — in which case no tab holds it and the caller should treat it as elsewhere.
 */
function bucketOf(session: SessionDetailDto | undefined): SessionTab | null {
  if (!session) return null;
  if (session.archived) return 'archived';

  return session.assignedToId === null ? 'unassigned' : 'assigned';
}

/** Zeroes a conversation's badge across every cached filter combination. */
function clearUnread(
  items: Paged<SessionListItemDto> | undefined,
  uuid: string,
): Paged<SessionListItemDto> | undefined {
  if (!items) return items;

  return {
    ...items,
    items: items.items.map((item) => (item.uuid === uuid ? { ...item, unread: 0 } : item)),
  };
}

export function ChatsPage(): React.JSX.Element {
  const { sessionUuid } = useParams<{ sessionUuid?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<SessionTab>('unassigned');
  const [detailsOpen, setDetailsOpen] = useState(false);

  /**
   * Live and archived conversations are fetched separately so every tab can
   * carry an accurate count and switching between them is instant from cache.
   * Both keys start with `['sessions']`, so the single invalidation the socket
   * already triggers refreshes them together.
   */
  const live = useQuery({
    queryKey: ['sessions', { search, archived: false }],
    queryFn: () =>
      api.get<Paged<SessionListItemDto>>('/sessions', {
        search: search || undefined,
        archived: false,
      }),
  });

  const archived = useQuery({
    queryKey: ['sessions', { search, archived: true }],
    queryFn: () =>
      api.get<Paged<SessionListItemDto>>('/sessions', {
        search: search || undefined,
        archived: true,
      }),
  });

  const unassignedItems = useMemo(
    () => (live.data?.items ?? []).filter((item) => item.category === 'new'),
    [live.data],
  );

  const assignedItems = useMemo(
    () => (live.data?.items ?? []).filter((item) => item.category === 'my'),
    [live.data],
  );

  const archivedItems = archived.data?.items ?? [];

  const visibleItems =
    tab === 'archived' ? archivedItems : tab === 'assigned' ? assignedItems : unassignedItems;

  // A count drawn from a full page is a floor rather than a total, so the tab
  // shows it as "3+" instead of implying nothing waits beyond the first page.
  const livePageFull = (live.data?.items.length ?? 0) >= SESSION_PAGE_SIZE;

  const counts: SessionCounts = {
    unassigned: countUnreadChats(unassignedItems),
    assigned: countUnreadChats(assignedItems),
    archived: countUnreadChats(archivedItems),
    capped: {
      unassigned: livePageFull,
      assigned: livePageFull,
      archived: archivedItems.length >= SESSION_PAGE_SIZE,
    },
  };

  const session = useQuery({
    queryKey: ['session', sessionUuid],
    queryFn: () => api.get<SessionDetailDto>(`/sessions/${sessionUuid as string}`),
    enabled: Boolean(sessionUuid),
  });

  const messages = useQuery({
    queryKey: ['messages', sessionUuid],
    queryFn: () => api.get<Paged<MessageDto>>('/messages', { sessionUuid }),
    enabled: Boolean(sessionUuid),
  });

  const presets = useQuery({
    queryKey: ['preset-responses'],
    queryFn: () => api.get<{ items: PresetResponse[] }>('/settings/preset-responses'),
    staleTime: 5 * 60_000,
  });

  const markRead = useMutation({
    mutationFn: (uuid: string) => api.post(`/sessions/${uuid}/read`),
    // Patch by the uuid the mutation actually ran with, taken from `variables`.
    // Reading it from the render closure instead patched whichever conversation
    // happened to be on screen when the request resolved, leaving the badge on
    // the one that was really marked read. `setQueriesData` covers every cached
    // search/archived combination, not just the one currently rendered.
    onSuccess: (_result, uuid) => {
      queryClient.setQueriesData<Paged<SessionListItemDto>>({ queryKey: ['sessions'] }, (previous) =>
        clearUnread(previous, uuid),
      );

      // The rows are patched above, but the rail's badge is a server-side
      // count and has just gone down by one. Refetched rather than decremented
      // here, because opening a conversation someone else had already read
      // changes nothing and a guess would drift.
      //
      // Through the same coalescer as everything else: an inbound message in
      // the open conversation runs this *and* the socket handler, so the
      // direct call this replaced meant two full recounts for one message.
      refreshUnread(queryClient);
    },
  });

  // Stable handles for use inside the socket callback, which must not be
  // re-created on every render (that would tear down the connection).
  const activeSessionRef = useRef<string | undefined>(sessionUuid);
  activeSessionRef.current = sessionUuid;

  const markReadRef = useRef(markRead.mutate);
  markReadRef.current = markRead.mutate;

  // Only what is particular to having a conversation open. The session lists
  // and the rail's badge are refreshed by RealtimeProvider, which is mounted
  // above this screen and hears the same events whether it is on screen or not.
  useRealtimeEvent((event) => {
    switch (event.type) {
      case 'message': {
        const active = activeSessionRef.current;
        if (event.message.sessionUuid !== active) return;

        queryClient.setQueryData<Paged<MessageDto>>(['messages', active], (previous) => {
          if (!previous) return previous;
          // The socket can echo a message the POST response already added.
          if (previous.items.some((item) => item.id === event.message.id)) return previous;

          return { ...previous, items: [...previous.items, event.message as MessageDto] };
        });

        // The agent is looking at this message right now, so it is read.
        // Without this the sidebar row they are reading accumulates a badge
        // that nothing clears until they navigate away and back.
        markReadRef.current(active);
        return;
      }

      case 'message-updated': {
        const active = activeSessionRef.current;
        if (event.message.sessionUuid !== active) return;

        // Replaces in place and adds nothing: this revises a message the
        // socket already delivered, and a conversation scrolled far enough
        // back may no longer be holding it.
        queryClient.setQueryData<Paged<MessageDto>>(['messages', active], (previous) => {
          if (!previous) return previous;

          return {
            ...previous,
            items: previous.items.map((item) =>
              item.id === event.message.id ? (event.message as MessageDto) : item,
            ),
          };
        });

        return;
      }

      case 'sessions-changed':
      case 'session-assigned':
        if (activeSessionRef.current) {
          void queryClient.invalidateQueries({ queryKey: ['session', activeSessionRef.current] });
        }
        return;

      default:
        return;
    }
  });

  const { status, send } = useRealtime();

  // Marking read is a plain HTTP call and must not depend on the socket — an
  // agent behind a proxy that blocks WebSocket upgrades could otherwise never
  // clear a badge.
  useEffect(() => {
    if (!sessionUuid) return;

    markReadRef.current(sessionUuid);
  }, [sessionUuid]);

  // Telling the server which conversation is on screen does need the socket.
  useEffect(() => {
    if (!sessionUuid || status !== 'open') return;

    send({ type: 'subscribe', sessionUuid });
  }, [sessionUuid, status, send]);

  const current = session.data;

  const handleSelect = (uuid: string): void => {
    navigate(`/chats/${uuid}`);
    setDetailsOpen(false);
  };

  /**
   * Switching tab closes whatever was open — unless the tab being opened is
   * the one that holds it.
   *
   * Leaving a conversation on screen after moving to a list it is not part of
   * reads as though the two belonged together; that is what this closes. But
   * the same gesture is also how an agent follows a conversation that has just
   * moved: press "Assign to me" and it leaves Unassigned for Assigned, and the
   * obvious next click is the tab it went to. Closing it there would shut the
   * thing they were reaching for. Same for archiving one and then looking in
   * Archived, and for arriving from a notification link, which always starts
   * on Unassigned however the conversation is filed.
   *
   * Replacing rather than pushing, because the tab is local state the URL does
   * not carry: a Back that restored the conversation but not the tab it
   * belonged to would land the reader somewhere neither of them describes.
   */
  const handleTabChange = (next: SessionTab): void => {
    // Pressing the tab you are already on is not a change, and must not close
    // the conversation you are reading.
    if (next === tab) return;

    setTab(next);
    setDetailsOpen(false);

    if (sessionUuid && bucketOf(current) !== next) navigate('/chats', { replace: true });
  };

  const sendMessage = useMutation({
    mutationFn: (input: {
      sessionUuid: string;
      content: string;
      attachments: AttachmentDto[];
      prefer?: 'sms' | 'email';
    }) => api.post<MessageDto>('/messages', input),
    // Key the cache write off the message's own conversation. A send can be slow
    // (the server tries SMS/email before responding), and if the agent has moved
    // on by then, writing into the on-screen key would render one customer's
    // reply inside another customer's transcript.
    onSuccess: (message) => {
      queryClient.setQueryData<Paged<MessageDto>>(['messages', message.sessionUuid], (previous) => {
        if (!previous) return previous;

        // Replaced, not skipped, when it is already there. The socket echoes
        // this message to its own sender and usually wins the race, but it is
        // broadcast before delivery is attempted — so that copy can never carry
        // the channel the reply went out by, and this one always does. Bailing
        // out left the agent's own send as the one message whose route only
        // appeared on the next load.
        const present = previous.items.some((item) => item.id === message.id);

        return {
          ...previous,
          items: present
            ? previous.items.map((item) => (item.id === message.id ? message : item))
            : [...previous.items, message],
        };
      });

      refreshSessions(queryClient);
    },
  });

  const claim = useMutation({
    mutationFn: (uuid: string) => api.post(`/sessions/${uuid}/claim`),
    onSuccess: () => {
      refreshSessions(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['session', sessionUuid] });
    },
  });

  const updateSession = useMutation({
    mutationFn: (patch: Partial<{ archived: boolean; spam: boolean }>) =>
      api.patch(`/sessions/${sessionUuid as string}`, patch),
    onSuccess: () => {
      refreshSessions(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['session', sessionUuid] });
    },
  });

  const markUnread = useMutation({
    mutationFn: (uuid: string) => api.post(`/sessions/${uuid}/unread`),
    onSuccess: () => {
      refreshSessions(queryClient);
      navigate('/chats');
    },
  });

  const canReply = Boolean(current) && !current?.archived;
  const isUnassigned = current?.assignedToId === null;

  return (
    <div className="flex h-full min-h-0">
      <SessionList
        items={visibleItems}
        loading={tab === 'archived' ? archived.isLoading : live.isLoading}
        activeUuid={sessionUuid}
        search={search}
        onSearch={setSearch}
        tab={tab}
        onTabChange={handleTabChange}
        counts={counts}
        onSelect={handleSelect}
        connectionStatus={status}
        className={sessionUuid ? 'hidden md:flex' : 'flex'}
      />

      <section className={`min-w-0 flex-1 flex-col ${sessionUuid ? 'flex' : 'hidden md:flex'}`}>
        {!sessionUuid ? (
          <EmptyState
            fill
            icon={<MessageSquareIcon className="h-12 w-12" />}
            title="No conversation selected"
            description="Pick a conversation from the list to read and reply."
          />
        ) : session.isLoading ? (
          <div className="flex flex-1 items-center justify-center text-brand-600">
            <Spinner className="h-6 w-6" />
          </div>
        ) : !current ? (
          <EmptyState fill title="Conversation not found" description="It may have been deleted." />
        ) : (
          <>
            <MessageThread
              session={current}
              messages={messages.data?.items ?? []}
              loading={messages.isLoading}
              currentUserId={me?.user.id ?? null}
              onBack={() => {
                navigate('/chats');
              }}
              detailsOpen={detailsOpen}
              onToggleDetails={() => {
                setDetailsOpen((open) => !open);
              }}
              onArchive={() => {
                updateSession.mutate({ archived: !current.archived });
              }}
              onSpam={() => {
                updateSession.mutate({ spam: true });
              }}
              onMarkUnread={() => {
                markUnread.mutate(current.uuid);
              }}
            />

            {isUnassigned ? (
              <div className="border-t border-slate-200 bg-amber-50 px-4 py-3 dark:border-slate-800 dark:bg-amber-950/40">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-amber-900 dark:text-amber-200">
                    Nobody has picked this up yet.
                  </p>
                  <button
                    type="button"
                    className="lg-btn-primary"
                    disabled={claim.isPending}
                    onClick={() => {
                      claim.mutate(current.uuid);
                    }}
                  >
                    {claim.isPending ? <Spinner /> : null}
                    Assign to me
                  </button>
                </div>
              </div>
            ) : null}

            {/*
              Keyed on the conversation: the composer holds an unsent draft and a
              channel override in local state, and switching to an already-cached
              conversation does not unmount this subtree. Without the key, a draft
              written to one customer would be sent to the next one opened.
            */}
            <Composer
              key={current.uuid}
              disabled={!canReply}
              disabledReason={current.archived ? 'This conversation is archived.' : undefined}
              sending={sendMessage.isPending}
              sessionType={current.type}
              presets={presets.data?.items ?? []}
              sendWithEnter={me?.preferences.sendWithEnter ?? true}
              smsConfigured={me?.chat.smsConfigured ?? false}
              // `mutateAsync` so the composer keeps the draft when a send fails.
              onSend={(content, attachments, prefer) =>
                sendMessage.mutateAsync({ sessionUuid: current.uuid, content, attachments, prefer })
              }
            />
          </>
        )}
      </section>

      {current && detailsOpen ? (
        <SessionDetails
          session={current}
          onClose={() => {
            setDetailsOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
